import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { downloadBlob } from '../../utils/file.ts';
import { requestWithResponse } from '../axios.ts';

// The export endpoints answer with the XLSX file, its name in the Content-Disposition header. The
// fallback name is used when the header can't be read.
async function downloadXlsx(url: string, params: Record<string, string>, fallbackFileName: string) {
  const response = await requestWithResponse<Blob>({ url, method: 'GET', params, responseType: 'blob' });
  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  downloadBlob(response.data, getFileName(contentDisposition) ?? fallbackFileName);
}

// Named "<Fallnummer> - <Datum>.xlsx".
export function useExportContactTracingFile() {
  return useMutation({
    mutationFn: (caseId: string) => downloadXlsx('/api/export-file/contact-tracing', { caseId }, 'Kontaktliste.xlsx'),
  });
}

// Named "Screening <Auftrag> - <Datum>.xlsx".
export function useExportScreeningFile() {
  return useMutation({
    mutationFn: (screeningCaseId: string) =>
      downloadXlsx('/api/export-file/screening', { screeningCaseId }, 'Screening.xlsx'),
  });
}

// Prefers the UTF-8 `filename*` over the plain `filename`, which can't hold umlauts.
function getFileName(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) {
    return null;
  }
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      // Fall through to the plain file name.
    }
  }
  return /filename\s*=\s*"?([^";]+)"?/i.exec(contentDisposition)?.[1]?.trim() ?? null;
}

// With responseType blob the error body is a Blob too. It holds the error code as plain text,
// which getMessage translates.
export async function getExportFileErrorMessage(
  error: unknown,
  getMessage: (code: string | undefined) => string,
): Promise<string> {
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const code = (await error.response.data.text()).trim().replace(/^"|"$/g, '');
      return getMessage(code);
    } catch {
      // Fall through to the generic message.
    }
  }
  return getMessage(undefined);
}
